import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const OrgAdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('users');
  const navigate = useNavigate();

  const tabs = [
    { id: 'users', name: 'User Management' },
    { id: 'tasks', name: 'Task Management' },
    { id: 'settings', name: 'Organization Settings' },
  ];

  const handleAddUser = () => {
    navigate('/org-admin/users');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Organization Admin Dashboard</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage your organization's users, tasks, and settings
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mt-8 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-8">
        {activeTab === 'users' && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
              <h2 className="text-lg font-medium text-gray-900">User Management</h2>
              <button 
                onClick={handleAddUser}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                Add User
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      No users found
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Task Management</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <div className="bg-gray-50 px-4 py-5 shadow rounded-lg overflow-hidden sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Active Tasks</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">0</dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 shadow rounded-lg overflow-hidden sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Pending Reviews</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">0</dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 shadow rounded-lg overflow-hidden sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Completed Tasks</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">0</dd>
              </div>
            </div>
            <div className="mt-6">
              <h3 className="text-md font-medium text-gray-900 mb-4">Recent Tasks</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Task
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assigned To
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        No tasks found
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Organization Settings</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-md font-medium text-gray-900">General Information</h3>
                <div className="mt-4 space-y-4">
                  {/* Add organization info fields here */}
                </div>
              </div>
              <div>
                <h3 className="text-md font-medium text-gray-900">Security Settings</h3>
                <div className="mt-4 space-y-4">
                  {/* Add security settings here */}
                </div>
              </div>
              <div>
                <h3 className="text-md font-medium text-gray-900">Integration Settings</h3>
                <div className="mt-4 space-y-4">
                  {/* Add integration settings here */}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgAdminDashboard; 